import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, disables the button and marks it `aria-busy`. */
  loading?: boolean;
  /** Leading icon (e.g. a lucide icon element). */
  icon?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-ds-accent text-ds-onaccent hover:bg-ds-accent/90",
  secondary: "border-ds-line2 bg-ds-panel text-ds-ink hover:bg-ds-sunk",
  ghost: "bg-transparent text-ds-ink2 hover:bg-ds-sunk hover:text-ds-ink",
  danger: "border-ds-line2 bg-transparent text-ds-err hover:bg-ds-err-tint",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-[30px] px-2.5 text-[13px]",
  md: "h-[34px] px-3.5 text-[13.5px]",
};

/** Design-system button: `primary` (teal), `secondary`, `ghost`, `danger`; `sm` / `md`. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, icon, disabled, className, children, type = "button", ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      data-variant={variant}
      data-size={size}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-transparent font-ds font-semibold transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60",
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  );
});
